<template>
  <permission-gate
    moduleKey="cotizacion"
    :showNoEditAlert="true"
  >
    <template #default="{ canEdit }">
      <main-breadcrumb
        :items="items"
        showBackButton
      />
      <main-buttons-bar :align="'space-between'">
        <template #left>
          <custom-title>Edición de Cotización</custom-title>
        </template>
      </main-buttons-bar>

      <detail-container>
        <loading-overlay :show="isLoading" />

        <div class="detail-container">
          <el-row :gutter="24">
            <el-alert
              v-if="cotizacion.isDesactualizado"
              class="alerta-banner-dark"
              type="warning"
              effect="dark"
              show-icon
              :closable="false"
            >
              <template #default>
                <div class="banner-content">
                  <div class="banner-text">
                    <span class="banner-title">Versión de ET Desactualizada</span>
                    <span class="banner-description">
                      Esta cotización utiliza la versión <strong>V{{ cotizacion.VersionET }}</strong
                      >. Existe una versión más reciente (<strong
                        >V{{ cotizacion.versionVigente }}</strong
                      >) disponible.
                    </span>
                  </div>
                  <el-button
                    class="banner-button"
                    size="default"
                    @click="abrirDiferenciasEt"
                  >
                    <template #icon>
                      <IconVectorDifference />
                    </template>
                    Ver cambios
                  </el-button>
                </div>
              </template>
            </el-alert>
            <custom-data
              :label="'Nro. de Cotizacion'"
              :spanColumn="3"
              :data="cotizacion?.Codigo"
            />
            <el-col
              v-if="cotizacion.CodigoSolicitud"
              :span="3"
            >
              <div class="label">Nro. de Solicitud</div>
              <router-link
                :to="`/page/requests/edit/${cotizacion.CodigoSolicitud}`"
                class="solicitud-link"
              >
                {{ cotizacion.CodigoSolicitud }}
              </router-link>
            </el-col>
            <el-col
              v-if="cotizacion?.CodigoPropuesta"
              :span="3"
            >
              <div class="label">Nro. de Propuesta</div>
              <router-link
                :to="`/page/proposals/edit/${cotizacion.CodigoPropuesta}`"
                class="solicitud-link"
              >
                {{ cotizacion.CodigoPropuesta }}
              </router-link>
            </el-col>
            <el-col :span="7">
              <div class="custom-label">Cliente/Prospecto</div>
              <el-autocomplete
                v-model="cotizacion.Cliente.Descripcion"
                size="default"
                :value-key="'Descripcion'"
                :class="{ 'input-error': !validarCotizacionCampos.Cliente }"
                :fetch-suggestions="handleSearch"
                clearable
                placeholder="Ingresar el cliente"
                style="width: 100%"
                @select="handleClientSelect"
              />
            </el-col>
            <custom-select
              v-model="cotizacion.EstadoSolicitud"
              :label="'Estado'"
              :spanColumn="4"
              :settings="{
                placeholder: 'Seleccionar estado',
                options: estadoSolicitudList,
                disabled: true,
              }"
              @update:modelValue="handleEstadoChange"
            />
            <custom-data
              :label="'Fecha estado'"
              :spanColumn="3"
              :data="cotizacion.FechaEstado ? getDate('dd/mm/yyyy', cotizacion.FechaEstado) : ''"
            />
            <custom-data
              :label="'Fecha creacion'"
              :spanColumn="3"
              :data="
                cotizacion.FechaCreacion ? getDate('dd/mm/yyyy', cotizacion.FechaCreacion) : ''
              "
            />
            <custom-data
              :label="'Fecha Modificación'"
              :spanColumn="3"
              :data="
                cotizacion.FechaActualizacion
                  ? getDate('dd/mm/yyyy', cotizacion.FechaActualizacion)
                  : ''
              "
            />
            <custom-data
              :label="'Vendedor'"
              :spanColumn="3"
              :data="`${cotizacion.Cliente?.UsuarioVendedorPrincipal?.Nombre || ''} ${
                cotizacion.Cliente?.UsuarioVendedorPrincipal?.Apellido || ''
              }`"
            />
          </el-row>
          <el-row :gutter="24">
            <custom-input
              v-model="cotizacion.Referencia"
              :label="'Descripción'"
              :spanColumn="10"
              :settings="{
                inputClass: { 'input-error': !validarCotizacionCampos.Referencia },
                placeholder: 'Ingrese la descripción del producto',
              }"
            />
            <custom-select
              v-model="cotizacion.Moneda"
              :label="'Moneda'"
              :spanColumn="4"
              :settings="{
                placeholder: 'Seleccionar moneda',
                inputClass: { 'input-error': !validarCotizacionCampos.CodigoMoneda },
                options: monedaList,
              }"
              @update:modelValue="handleMonedaChange"
            />

            <custom-input-number
              v-model="cotizacion.TasaDolarCompra"
              class="input-number"
              :label="'Compra'"
              :spanColumn="3"
              :settings="{
                placeholder: `Compra`,
                min: 0,
                precision: 2,
                step: 0.01,
                controls: false,
                inputClass: { 'input-error': !validarCotizacionCampos.TasaDolarCompra },
              }"
            />

            <custom-input-number
              v-model="cotizacion.TasaDolarVenta"
              class="input-number"
              :label="'Venta'"
              :spanColumn="3"
              :settings="{
                placeholder: `Venta`,
                min: 0,
                precision: 2,
                step: 0.01,
                controls: false,
                inputClass: { 'input-error': !validarCotizacionCampos.TasaDolarVenta },
              }"
            />
          </el-row>
          <el-row :gutter="24">
            <el-col :span="4">
              <div class="custom-label">Adjuntos</div>
              <el-upload
                class="upload-list"
                :file-list="fileList"
                :auto-upload="true"
                multiple
                :http-request="uploadAdjuntoCotizacion"
                accept=".jpg,.png,.pdf,.txt"
                :on-remove="handleRemoveCotizacion"
                :on-preview="handlePreviewAdjunto"
              >
                <el-button
                  class="button-container-upload"
                  type="primary"
                  size="default"
                  ><IconUpload></IconUpload> Subir adjuntos</el-button
                >
              </el-upload>
            </el-col>
            <custom-select
              v-model="cotizacion.Usabilidad"
              :label="'Usabilidad'"
              :spanColumn="4"
              :settings="{
                placeholder: 'Seleccionar aplicación',
                options: usabilidadLista,
              }"
              @update:modelValue="handleUsabilidadChange"
            />
            <custom-select
              v-model="cotizacion.UnidadVenta"
              :label="'Unidad venta'"
              :spanColumn="6"
              :settings="{
                placeholder: 'Seleccionar unidad',
                inputClass: { 'input-error': !validarCotizacionCampos.CodigoUnidadVenta },
                options: unidadesMedida,
              }"
              @update:modelValue="handleUnidadVentaChange"
            />
          </el-row>
          <el-row
            :gutter="24"
            style="margin-top: 20px"
          >
            <el-col>
              <div class="custom-label">Cantidades a cotizar</div>
              <div class="quantity-inputs">
                <el-button
                  class="el-button-quantity"
                  type="primary"
                  size="default"
                  :icon="IconAdd"
                  @click="addQuantity"
                />
                <el-button
                  type="danger"
                  size="default"
                  :icon="IconDelete"
                  @click="removeQuantity"
                ></el-button>
                <div class="container-quantity-inputs">
                  <custom-input-number
                    v-for="(quantity, index) in cotizacion.CotizacionCantidad"
                    :key="index"
                    v-model="cotizacion.CotizacionCantidad[index].Cantidad"
                    class="input-number"
                    :spanColumn="3"
                    :settings="{
                      placeholder: `Cantidad ${index + 1}`,
                      min: 0,
                      precision: precisionCantidades,
                      step: 0.01,
                      controls: false,
                    }"
                    @blur="validateQuantity(index)"
                    @change="pricesStore.autoCompletarCantidadUnica(index)"
                  />
                </div>
              </div>
            </el-col>
          </el-row>

          <InputProductosTerminados
            v-if="ElementoTipoSeleccionado.Codigo"
            :productos="cotizacion.CotizacionProducto"
            :codigoCliente="(cotizacion.Cliente && cotizacion.Cliente.Codigo) || null"
            :codigoElementoGrupo="elementoGrupo?.Codigo || null"
            :codigoUnidadVenta="cotizacion.CodigoUnidadVenta || null"
            :validacionesCantidades="pricesStore.validarSumatoriaCantidades"
            :precision="precisionCantidades"
            @add-producto="pricesStore.addProducto"
            @remove-producto="pricesStore.removeProducto"
            @update-producto="pricesStore.updateProducto"
          />

          <input-deliveries
            :deliveries="cotizacion.CotizacionEntregas"
            :precision="precisionCantidades"
            @add-delivery="pricesStore.addEntrega"
            @remove-delivery="pricesStore.removeEntrega"
            @update-delivery="pricesStore.updateEntrega"
          />

          <div
            v-for="(elemento, index) in modelo"
            :key="index"
          >
            <component
              :is="getComponenteDinamico(elemento.ElementoTipo.CodigoElementoBase)"
              v-if="configReady && elemento.ElementoTipo?.CodigoElementoBase"
              v-model="modelo[index]"
              :title="`${elemento.ElementoTipo.Descripcion} ${index + 1}`"
              :name="`${elemento.ElementoTipo.Descripcion}${index}`"
            />
          </div>

          <el-row :gutter="20">
            <custom-input
              v-model="cotizacion.ObservacionesSolicitud"
              :label="'Observaciones de Solicitud'"
              :spanColumn="8"
              :settings="{
                placeholder: 'Información proveniente de la Solicitud',
                maxlength: 800,
                showWordLimit: true,
              }"
            />

            <custom-input
              v-model="cotizacion.Observaciones"
              :label="'Observaciones para ET / OT'"
              :spanColumn="8"
              :settings="{
                placeholder: 'Observaciones para ET / OT',
                maxlength: 800,
                showWordLimit: true,
              }"
            />

            <custom-input
              v-model="cotizacion.Notas"
              :label="'Notas Internas'"
              :spanColumn="8"
              :settings="{
                placeholder: 'Notas de uso local ',
                maxlength: 800,
                showWordLimit: true,
              }"
            />
          </el-row>

          <div v-if="!habilitarNuevaGrillaCostos">
            <el-tabs
              v-model="activeTab"
              type="card"
              class="product-tabs"
            >
              <el-tab-pane
                label="Producto"
                name="Producto"
              >
                <el-table :data="cotizacion.CotizacionCantidadCostos">
                  <el-table-column
                    fixed
                    prop="Descripcion"
                    label="Costo"
                    width="240"
                  ></el-table-column>
                  <el-table-column
                    fixed
                    prop="MedidaAbreviada"
                    label="Medida"
                    align="right"
                    width="80"
                    class-name="custom-quantity-column"
                  ></el-table-column>
                  <el-table-column
                    v-for="(item, index) in cotizacion.CotizacionCantidad"
                    :key="index"
                    :label="`Cantidad ${index + 1}:  ${$formatDecimal(item.Cantidad, decimalesImportes)}`"
                    class-name="custom-quantity-column"
                  >
                    <el-table-column
                      :prop="`Cantidad${index}`"
                      label="Cantidad"
                      width="150"
                      align="right"
                    >
                      <template #default="scope">
                        <div class="grilla-input-wrapper">
                          <span v-if="scope.row.Utilidad !== undefined">
                            <custom-input-number
                              v-model="cotizacion.CotizacionCantidad[index].Utilidad"
                              :spanColumn="24"
                              unit="%"
                              :settings="{
                                controls: false,
                                precision: 2,
                                size: 'small',
                              }"
                              :disabled="
                                [
                                  EstadoSolicitud.CON_PROPUESTA,
                                  EstadoSolicitud.ACEPTADA,
                                  EstadoSolicitud.RECHAZADA,
                                ].includes(cotizacion.CodigoEstado)
                              "
                            />
                          </span>
                          <span v-else-if="scope.row.Comision !== undefined">
                            <custom-input-number
                              v-model="cotizacion.CotizacionCantidad[index].Comision"
                              :spanColumn="24"
                              unit="%"
                              :settings="{
                                controls: false,
                                precision: 2,
                                size: 'small',
                              }"
                              :disabled="
                                [
                                  EstadoSolicitud.CON_PROPUESTA,
                                  EstadoSolicitud.ACEPTADA,
                                  EstadoSolicitud.RECHAZADA,
                                ].includes(cotizacion.CodigoEstado)
                              "
                            />
                          </span>
                          <span v-else-if="scope.row.Flete !== undefined">
                            <custom-input-number
                              v-model="cotizacion.CotizacionCantidad[index].PorcentajeFlete"
                              :spanColumn="24"
                              unit="%"
                              :settings="{
                                controls: false,
                                precision: 2,
                                size: 'small',
                              }"
                            />
                          </span>
                          <span v-else-if="scope.row.MermaAdicional !== undefined">
                            <custom-input-number
                              v-model="cotizacion.CotizacionCantidad[index].MermaAdicional"
                              :spanColumn="24"
                              unit="%"
                              :settings="{
                                controls: false,
                                precision: 2,
                                size: 'small',
                              }"
                            />
                          </span>
                          <span v-else-if="scope.row.PrecioObjetivo">
                            <custom-input-number
                              v-model="cotizacion.CotizacionCantidad[index].PrecioObjetivo"
                              :spanColumn="24"
                              :settings="{
                                controls: false,
                                precision: 2,
                                size: 'small',
                              }"
                            />
                          </span>
                          <span v-else-if="scope.row.AlicuotaIVA !== undefined">
                            <custom-input-number
                              v-model="cotizacion.CotizacionCantidad[index].AlicuotaIVA"
                              :spanColumn="24"
                              unit="%"
                              :settings="{
                                controls: false,
                                precision: 2,
                                size: 'small',
                              }"
                              :disabled="true"
                            />
                          </span>

                          <span v-else>
                            {{
                              $formatDecimal(scope.row[`Cantidad${index + 1}`], decimalesImportes)
                            }}
                            {{
                              scope.row.Descripcion &&
                              scope.row.Descripcion.toString().includes('calculada')
                                ? '%'
                                : ''
                            }}
                          </span>
                        </div>
                      </template>
                    </el-table-column>
                    <el-table-column
                      :prop="`Precio${index}`"
                      label="Costo"
                      width="120"
                      align="right"
                    >
                      <template #default="scope">
                        {{ $formatDecimal(scope.row[`Precio${index + 1}`], decimalesImportes) }}
                      </template>
                    </el-table-column>
                    <el-table-column
                      :prop="`PrecioUnitario${index}`"
                      label="Costo Unitario"
                      width="120"
                      align="right"
                      class-name="custom-quantity-column"
                    >
                      <template #default="scope">
                        {{
                          $formatDecimal(scope.row[`PrecioUnitario${index + 1}`], decimalesImportes)
                        }}
                      </template>
                    </el-table-column>
                    <el-table-column
                      v-if="mostrarValorUnidadVentaGrilla"
                      :prop="`ValorUnidadVenta${index + 1}`"
                      label="Valor por Unidad de Venta"
                      width="140"
                      align="right"
                    >
                      <template #default="scope">
                        {{
                          scope.row[`ValorUnidadVenta${index + 1}`] !== ''
                            ? $formatDecimal(
                                scope.row[`ValorUnidadVenta${index + 1}`],
                                decimalesImportes,
                              )
                            : ''
                        }}
                      </template>
                    </el-table-column>

                    <el-table-column
                      v-if="mostrarValorUnidadProductoGrilla"
                      :prop="`ValorUnidadProducto${index + 1}`"
                      label="Valor por Unidad de Producto"
                      width="140"
                      align="right"
                    >
                      <template #default="scope">
                        {{
                          scope.row[`ValorUnidadProducto${index + 1}`] !== ''
                            ? $formatDecimal(
                                scope.row[`ValorUnidadProducto${index + 1}`],
                                decimalesImportes,
                              )
                            : ''
                        }}
                      </template>
                    </el-table-column>
                  </el-table-column>
                </el-table>
              </el-tab-pane>
            </el-tabs>
          </div>

          <nueva-grilla-costos
            v-else
            :cotizacion="cotizacion"
            @recargar-cotizacion="handleRecargarCotizacionCostos"
          />

          <div
            v-if="canEdit"
            class="container container--nueva-grilla"
          >
            <div
              v-if="!cotizacion.Codigo"
              style="margin-right: 1%"
            >
              <el-button
                type="primary"
                size="default"
                @click="sendCotizarNuevo"
              >
                <IconPeso />Crear cotización</el-button
              >
            </div>
            <div
              v-if="permiteCopiarCotizacion"
              class="accion-copiar-cotizacion"
              style="margin-right: 1%"
            >
              <el-button
                type="primary"
                size="default"
                @click="copiarCotizacion"
              >
                <IconRefresh /> Copiar cotización
              </el-button>
            </div>
            <div
              v-if="permiteCotizar"
              class="accion-cotizar"
              style="margin-right: 1%"
            >
              <el-button
                type="success"
                size="large"
                @click="sendCotizarNuevo"
              >
                <IconPeso /> Cotizar</el-button
              >
            </div>

            <el-button
              v-if="cotizacion.CodigoEstado === EstadoSolicitud.COTIZADA"
              type="primary"
              size="default"
              :icon="IconFileDocumentAddOutline"
              @click="showPrePropuestaComercial"
              >Crear Propuesta comercial</el-button
            >
            <el-button
              v-if="cotizacion.CodigoEstado === EstadoSolicitud.CON_PROPUESTA"
              type="primary"
              size="default"
              @click="printPdfPropuestaComercial"
              ><IconPrint />&nbsp;Propuesta comercial</el-button
            >
            <el-button
              type="primary"
              size="default"
              :disabled="esEjecutivoCotiza"
              @click="printPdfCostoInterno"
              ><IconPrint />&nbsp;Costeo interno</el-button
            >
            <el-button
              v-if="cotizacion.Codigo"
              type="primary"
              size="default"
              :icon="IconChartTimelineVariantShimmer"
              @click="verResultadosProduccion"
            >
              Ver cálculos productivos
            </el-button>
            <el-button
              v-if="permiteActualizarSolicitud"
              type="primary"
              size="default"
              @click="onUpdateSolicitud"
            >
              <IconRefresh />&nbsp;Actualizar Solicitud
            </el-button>
          </div>
        </div>
      </detail-container>

      <el-dialog
        v-model="isModalPreProposalVisible"
        title="Resumen propuesta comercial"
        width="85%"
      >
        <ProposalsListItems
          :data="prePropuestaComercial"
          @deleteItem="deleteItem"
        />
        <el-button
          v-if="cotizacion.CodigoEstado === EstadoSolicitud.COTIZADA"
          type="primary"
          size="default"
          :icon="IconFileDocumentAddOutline"
          @click="createPropuestaComercial"
          >Crear Propuesta comercial</el-button
        >
      </el-dialog>

      <el-dialog
        v-model="isModalPdfVisible"
        title="Previsualización - Costeo Interno"
        width="80%"
        top="5vh"
        destroy-on-close
      >
        <div
          v-loading="isLoadingPdf"
          element-loading-text="Generando previsualización..."
          style="min-height: 600px"
        >
          <iframe
            v-show="!isLoadingPdf"
            v-if="pdfUrl"
            :src="pdfUrl"
            width="100%"
            height="600px"
            frameborder="0"
            @load="onPdfLoaded"
          ></iframe>
        </div>
        <template #footer>
          <div class="dialog-footer">
            <el-button @click="isModalPdfVisible = false">Cancelar</el-button>
            <el-button
              type="primary"
              :disabled="isLoadingPdf"
              @click="descargarPdf"
            >
              <IconPrint /> Imprimir / Descargar PDF
            </el-button>
          </div>
        </template>
      </el-dialog>

      <modal-duplicate-price
        v-model="modalDuplicateVisible"
        :isObsoleta="cotizacion.isDesactualizado"
        @confirm="ejecutarClonacion"
      />

      <et-diferencias-modal
        v-model="modalDiferenciasEtVisible"
        :diferencias="diferenciasEt"
        :bloqueado-por-propuesta="bloqueadoPorPropuestaEt"
        :loading="loadingActualizarEt"
        @update="actualizarEt"
      />

      <floating-info-panel
        :panel-state="floatingPanel.panelState"
        :is-pinned="floatingPanel.isPinned"
        :is-minimized="floatingPanel.isMinimized"
        :active-tab="floatingPanel.activeTab"
        :datos-producto="resultadoProduccionStore.datosProducto"
        :datos-producto-live="datosProductoLive"
        :procesos="resultadoProduccionStore.resultadoDetalle?.procesos || []"
        :materiales="resultadoProduccionStore.resultadoDetalle?.materiales || []"
        :cantidades="resultadoProduccionStore.cantidades || []"
        :cantidad-seleccionada="resultadoProduccionStore.cantidadSeleccionada"
        :is-stale="resultadoProduccionStore.isStale"
        :loading="resultadoProduccionStore.loading"
        @show="floatingPanel.show"
        @hide="floatingPanel.hide"
        @hide-now="floatingPanel.hideNow"
        @toggle-pin="floatingPanel.togglePin"
        @toggle-minimize="floatingPanel.toggleMinimize"
        @cantidad-change="handleFloatingCantidadChange"
        @tab-change="floatingPanel.setActiveTab"
      />
    </template>
  </permission-gate>
</template>

<script setup>
import {
  ref,
  onBeforeMount,
  computed,
  onMounted,
  onBeforeUnmount,
  provide,
  watch,
  nextTick,
  reactive,
} from 'vue';
import {
  useRoute,
  useRouter,
} from 'vue-router';
import { storeToRefs } from 'pinia';
import { usePricesStore } from '../stores/prices_store.js';
import { useProspectStore } from '@/views/prospects/stores/prospect_store.js';
import { useRequestStore } from '@/views/requests/stores/request_store.js';
import { useMonedaStore } from '@/stores/moneda/moneda_store';
import { useProposalsStore } from '../../proposals/proposals_store';
import { useEstadoSolicitudStore } from '@/stores/estado-solicitud.js';
import { useConfiguracionStore } from '../../../stores/configuracion-general.js';
import { useConfiguracionesPorDefectoStore } from '@/views/configuraciones_por_defecto/stores/configuraciones_por_defecto_store.js';
import { useUsabilidadStore } from '@/stores/usabilidad/usabilidad_store.js';
import { useUnidadMedidaStore } from '@/stores/unidad_medida/unidad_medida_store.js';
import { useMateriaPrimaFamiliaStore } from '@/stores/materia_prima_familia/materia_prima_familia_store';
import { useElementoGrupoStore } from '@/stores/elemento_grupo/elemento_grupo_store';
import { FACTOR } from '@/shared/composables/useUnitConversion';
import { Modules } from '@/enum/modules';
import {
  precisionCantidad,
  redondearCantidadesLista,
} from '@/shared/utils/cantidad_unidad_venta.js';
import { ElementoBase } from '@/enum/elemento_base';
import { conAnchoMaterialExtrusion } from '@/shared/utils/extrusion_ancho_material.js';
import { mensajeErrorApi } from '@/shared/utils/error_api.js';
import { resolveApiUrl } from '@/shared/utils/resolve_api_url.js';
import { describirCamposInvalidos } from '@/shared/utils/validacion_campos.js';
import CustomData from '@/shared/components/custom_data.vue';
import CustomTitle from '@/shared/components/custom_title.vue';
import CustomSelect from '@/shared/components/custom_select.vue';
import DetailContainer from '@/shared/layout/detail/detail_container.vue';
import LoadingOverlay from '@/shared/LoadingOverlay.vue';
import IconPeso from '~icons/mdi/dollar';
import IconPrint from '~icons/mdi/printer-outline';
import IconUpload from '~icons/mdi/cloud-upload';
import IconAdd from '~icons/mdi/add';
import IconDelete from '~icons/mdi/delete';
import IconVectorDifference from '~icons/mdi/vector-difference-ba';
import IconFileDocumentAddOutline from '~icons/mdi/file-document-add-outline';
import IconRefresh from '~icons/mdi/file-refresh-outline';
import MainBreadcrumb from '@/shared/layout/main/main_breadcrum.vue';
import MainButtonsBar from '@/shared/layout/main/buttons_bar/main_buttons_bar.vue';
import getDate from '@/shared/components/utils.js';
import CustomInputNumber from '@/shared/components/custom_input_number.vue';
import ProposalsListItems from '@/shared/components/proposals_list_items.vue';
import CustomInput from '@/shared/components/custom_input.vue';
import InputProductosTerminados from '@/shared/components/input_productos_terminados.vue';
import InputDeliveries from '@/shared/components/input_deliveries.vue';
import NuevaGrillaCostos from './components/nueva_grilla_costos.vue';
import { ElNotification } from 'element-plus';
import { useAdjuntosUpload } from '@/shared/composables/useAdjuntosUpload.js';
import { useMessageError } from '@/shared/composables/message_error.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';
import { useLoading } from '@/shared/composables/loading.js';
import { obtenerCodigoUsuario } from '@/shared/utils/get_user_code.js';
import { useAuthStore } from '@/views/auth/auth_store';
import { provideErrorRegistry } from '@/shared/composables/use_error_registry.js';
import { notificarErroresValidacion } from '@/shared/utils/notificar_errores_validacion.js';
import { EstadoSolicitud } from '@/enum/estado_solicitud';
import prices_elemento_model from '../models/prices_elemento_model.js';
import { useElementos } from '@/shared/composables/useElementos.js';
import { useModulo } from '@/shared/composables/useModulo.js';
import { usePrint } from '@/shared/composables/print.js';
import PermissionGate from '@/shared/components/permission_gate.vue';
import { useElementoBaseStore } from '@/stores/elemento_base/elemento_base_store';
import ModalDuplicatePrice from '@/shared/components/modal_duplicate_price.vue';
import EtDiferenciasModal from '@/shared/components/et_diferencias_modal.vue';
import proceso_service from '@/services/proceso_service';
import FloatingInfoPanel from '@/shared/components/floating_info_panel.vue';
import { useFloatingPanel } from '@/shared/composables/use_floating_panel.js';
import { useAnchoMinimoMM } from '@/shared/composables/use_ancho_minimo.js';
import { useResultadoProduccionStore } from '../resultado_produccion/stores/resultado_produccion_store.js';
import IconChartTimelineVariantShimmer from '~icons/mdi/chart-timeline-variant-shimmer';

provide('currentModule', Modules.COTIZACION);
const elementoBaseEtiquetas = computed(
  () => elementoBaseStore.elementoBaseConfig?.etiquetas ?? null,
);
provide('elementoBaseEtiquetas', elementoBaseEtiquetas);

const { tieneErrores, mensajes: mensajesErrores } = provideErrorRegistry();

const route = useRoute();
const router = useRouter();
const pricesStore = usePricesStore();
const prospectStore = useProspectStore();
const requestStore = useRequestStore();
const monedaStore = useMonedaStore();
const configuracionStore = useConfiguracionStore();
const configuracionesPorDefectoStore = useConfiguracionesPorDefectoStore();
const estadoSolicitudStore = useEstadoSolicitudStore();
const usabilidadStore = useUsabilidadStore();
const unidadMedidaStore = useUnidadMedidaStore();
const materiaPrimaFamiliaStore = useMateriaPrimaFamiliaStore();

const elementoGrupoStore = useElementoGrupoStore();
const resultadoProduccionStore = useResultadoProduccionStore();
const floatingPanel = reactive(useFloatingPanel());
const { uploadPublicFileHTTP } = configuracionStore;
const { decimalesImportes } = storeToRefs(configuracionStore);
provide('decimalesImportes', decimalesImportes);
const { elementoGrupo } = storeToRefs(elementoGrupoStore);

// Obtener componentes para el módulo actual
const { getComponenteDinamico } = useModulo(Modules.COTIZACION);
const { getDataReportByName } = usePrint();

import { replayLogs } from '@/shared/utils/replay_log.js';

const {
  cotizacion,
  validarCotizacionCampos,
  isModalPreProposalVisible,
  prePropuestaComercial,
  primerMaterialImpresion,
} = storeToRefs(pricesStore);
// Ver work_order_edit.vue: el backend agrupa los materiales ya elegidos en todo el documento
// por categoría, e input_materiales.vue lo inyecta para poblar sus combos remotos con lo ya
// seleccionado sin depender de qué página de la búsqueda tenga cargada.
const materialesSeleccionadosPorCategoria = computed(
  () => cotizacion.value?.MaterialesSeleccionadosPorCategoria ?? {},
);
provide('materialesSeleccionadosPorCategoria', materialesSeleccionadosPorCategoria);

const { monedaList } = storeToRefs(monedaStore);
const { unidadesMedida } = storeToRefs(unidadMedidaStore);
const precisionCantidades = computed(() => precisionCantidad(cotizacion.value.CodigoUnidadVenta));
const {
  findCotizacionPorCodigoHTTP,
  // cotizarHTTP,
  validarCamposCotizacion,
  createPrePropuestaComercial,
  setProductTypeInPrice,
  resetCotizacion,
  createPriceHTTP,
  newPriceNuevoHTTP,
  // updateRequest,
  resetValidaciones,
  updateSolicitud,
  getDiferenciasEtHTTP,
  actualizarEtHTTP,
} = pricesStore;

const {
  mostrarValorUnidadVentaGrilla,
  mostrarValorUnidadProductoGrilla,
  sellado,
  solapa,
  habilitarNuevaGrillaCostos,
} = storeToRefs(configuracionStore);
const { utilidadGeneral, porcentajeIvaPredeterminado } = storeToRefs(
  configuracionesPorDefectoStore,
);
const { usabilidadLista } = storeToRefs(usabilidadStore);
const elementoBaseStore = useElementoBaseStore();

const { searchProspectsHTTP } = prospectStore;
const { firstEstadoSolicitud } = estadoSolicitudStore;
const { estadoSolicitudList } = storeToRefs(estadoSolicitudStore);
const { firstMoneda } = monedaStore;
const activeTab = ref('Producto');
const isModalPdfVisible = ref(false);
const pdfUrl = ref('');
const isLoadingPdf = ref(false);
const modalDuplicateVisible = ref(false);

const ElementoTipoSeleccionado = ref({});
const previousProductCodes = ref(null);
const configReady = ref(false);
const { modelo, initializeModelo, isLoading, standardToModuleArray } = useElementos();

const items = [
  { label: 'Inicio', to: '/page/prices/list', replace: false },
  { label: 'Detalle', to: null, replace: false },
];

const uploadToken = ref((crypto?.randomUUID && crypto.randomUUID()) || String(Date.now()));
const API_BASE = import.meta.env.VITE_URL_BASE_API || import.meta.env.VITE_URL_BASE || '';

const cotizacionAdjuntoAdapter = {
  getAdjuntos: () => cotizacion.value.CotizacionAdjunto,
  setAdjuntos: (next) => (cotizacion.value.CotizacionAdjunto = next),

  getSubdir: () => {
    const code = cotizacion.value?.Codigo;
    return code ? `adjuntos/cotizacion` : `adjuntos/cotizacion/tmp/${uploadToken.value}`;
  },

  buildAdjunto: ({ relPath, nextItem }) => ({
    Codigo: null,
    CodigoCotizacion: cotizacion.value?.Codigo ?? null,
    ItemAdjunto: nextItem,
    Url: relPath,
  }),
};

const {
  fileList,
  uploadAdjunto: uploadAdjuntoCotizacion,
  handleRemove: handleRemoveCotizacion,
} = useAdjuntosUpload({
  uploadPublicFileHTTP,
  apiBase: API_BASE,
  adapter: cotizacionAdjuntoAdapter,
});

const handlePreviewAdjunto = (file) => {
  const url = file?.url;
  if (!url) return;
  window.open(url, '_blank', 'noopener');
};
// const extrusionRef = ref(null)

const authStore = useAuthStore();
// Rol "Ejecutivos que cotizan": no puede ver el costeo interno.
const esEjecutivoCotiza = computed(() => authStore.esEjecutivoQueCotiza());

const permiteCotizar = computed(() => {
  return (
    cotizacion.value &&
    [EstadoSolicitud.PENDIENTE, EstadoSolicitud.COTIZADA, EstadoSolicitud.A_COTIZAR].includes(
      cotizacion.value.CodigoEstado,
    )
  );
});

const permiteCopiarCotizacion = computed(() => {
  const c = cotizacion.value;
  return !!(
    c &&
    c.Codigo &&
    c.CodigoSolicitud &&
    (c.CodigoEstado === EstadoSolicitud.COTIZADA ||
      c.CodigoEstado === EstadoSolicitud.CON_PROPUESTA ||
      c.CodigoEstado === EstadoSolicitud.ACEPTADA)
  );
});

const permiteActualizarSolicitud = computed(() => {
  const c = cotizacion.value;
  return !(
    c &&
    c.Codigo &&
    c.CodigoSolicitud &&
    (c.CodigoEstado === EstadoSolicitud.CON_PROPUESTA ||
      c.CodigoEstado === EstadoSolicitud.ACEPTADA)
  );
});

const handleSearch = async (query, cb) => {
  let results = [];
  if (query && query.length >= 3 && query !== 'null') {
    results = await searchProspectsHTTP(query);
  }
  cb(results ?? []);
};

const handleClientSelect = (item) => {
  if (item && item.Codigo) {
    cotizacion.value.Cliente = item;
    cotizacion.value.CodigoCliente = item.Codigo;
    aplicarUtilidadYComision();
  }
};

const validateQuantity = (index) => {
  const value = cotizacion.value.CotizacionCantidad[index];
  if (value.Cantidad === 0 || value.Cantidad === null || value.Cantidad < 0) {
    ElNotification({
      title: 'Cantidades a cotizar',
      message: 'Debe ingresar una cantidad',
      type: 'warning',
    });
  }
};
const addQuantity = () => {
  const cliente = cotizacion.value.Cliente;
  let utilidadDefault = 0;
  let comisionDefault = 0;

  if (cliente) {
    utilidadDefault = cliente.Utilidad ?? utilidadGeneral.value;
    comisionDefault = cliente.UsuarioVendedorPrincipal?.Comision ?? 0;
  }

  if (!cotizacion.value.CotizacionCantidad) cotizacion.value.CotizacionCantidad = [];
  const newQuantity = {
    Codigo: cotizacion.value.CotizacionCantidad.length,
    CodigoSolicitud: 0,
    ItemCantidad: cotizacion.value.CotizacionCantidad.length + 1,
    Cantidad: null,
    Utilidad: utilidadDefault,
    Comision: comisionDefault,
  };
  cotizacion.value.CotizacionCantidad.push(newQuantity);
  if (cotizacion.value.CotizacionProducto) {
    cotizacion.value.CotizacionProducto.forEach((prod) => {
      if (!prod.CotizacionProductoCantidad) prod.CotizacionProductoCantidad = [];
      prod.CotizacionProductoCantidad.push({
        Cantidad: null,
        ItemCantidad: newQuantity.ItemCantidad,
      });
    });
  }
};

const removeQuantity = () => {
  if (cotizacion.value.CotizacionCantidad.length > 1) {
    cotizacion.value.CotizacionCantidad.pop();
    if (cotizacion.value.CotizacionProducto) {
      cotizacion.value.CotizacionProducto.forEach((prod) => {
        if (prod.CotizacionProductoCantidad && prod.CotizacionProductoCantidad.length > 0) {
          prod.CotizacionProductoCantidad.pop();
        }
      });
    }
  } else {
    ElNotification({
      title: 'Cantidades',
      message: 'Debe ingresar al menos una cantidad a cotizar',
      type: 'warning',
    });
  }
};

// Método para manejar el cambio de Moneda
function handleMonedaChange(item) {
  cotizacion.value.CodigoMoneda = item.Codigo;

  // Si no es la moneda con código 1, actualizar las tasas con los valores de la moneda
  if (item.Codigo !== 1) {
    cotizacion.value.TasaDolarCompra = item.CotizacionCompra || 0;
    cotizacion.value.TasaDolarVenta = item.CotizacionVenta || 0;
  } else {
    cotizacion.value.TasaDolarCompra = 0;
    cotizacion.value.TasaDolarVenta = 0;
  }
}

// Método para manejar el cambio de Usabilidad
function handleUsabilidadChange(item) {
  cotizacion.value.CodigoUsabilidad = item.Codigo;
}
// Método para manejar el cambio de Unidad Venta
function handleUnidadVentaChange(item) {
  cotizacion.value.CodigoUnidadVenta = item?.Codigo ?? null;
  const precision = precisionCantidad(cotizacion.value.CodigoUnidadVenta);
  const productos = cotizacion.value.CotizacionProducto || [];
  const listas = [
    cotizacion.value.CotizacionCantidad,
    cotizacion.value.CotizacionEntregas,
    ...productos.map((p) => p.CotizacionProductoCantidad),
    productos,
  ];
  const huboCambios = listas
    .map((lista) => redondearCantidadesLista(lista, precision))
    .some(Boolean);
  if (huboCambios) {
    ElNotification({
      title: 'Cantidades',
      message: 'La unidad de venta no admite decimales: las cantidades se redondearon a enteros.',
      type: 'warning',
    });
  }
}

// Método para manejar el cambio de estado
function handleEstadoChange() {
  cotizacion.value.FechaEstado = getDate('yyyy/mm/dd');
  cotizacion.value.CodigoEstado = cotizacion.value.EstadoSolicitud.Codigo;
}

const aplicarUtilidadYComision = () => {
  const estadosProtegidos = [
    EstadoSolicitud.COTIZADA,
    EstadoSolicitud.ACEPTADA,
    EstadoSolicitud.CON_PROPUESTA,
    EstadoSolicitud.RECHAZADA,
  ];

  if (estadosProtegidos.includes(cotizacion.value.CodigoEstado)) {
    return;
  }

  const cliente = cotizacion.value.Cliente;
  if (!cliente) return;

  let utilidadCalculada = cliente.Utilidad;
  if (utilidadCalculada === null || utilidadCalculada === undefined || utilidadCalculada === '') {
    utilidadCalculada = utilidadGeneral.value;
  }

  let comisionCalculada = 0;
  if (cliente.UsuarioVendedorPrincipal && cliente.UsuarioVendedorPrincipal.Comision !== null) {
    comisionCalculada = Number(cliente.UsuarioVendedorPrincipal.Comision);
  }

  if (cotizacion.value.CotizacionCantidad && cotizacion.value.CotizacionCantidad.length > 0) {
    cotizacion.value.CotizacionCantidad.forEach((item) => {
      item.Utilidad = utilidadCalculada;
      item.Comision = comisionCalculada;
    });
  }
};

watch(
  () => cotizacion.value.CotizacionProducto,
  (productosSeleccionados) => {
    const estadoActual = cotizacion.value.CodigoEstado;
    if (
      estadoActual === EstadoSolicitud.CON_PROPUESTA ||
      estadoActual === EstadoSolicitud.ACEPTADA
    ) {
      return;
    }

    if (!productosSeleccionados) return;

    const currentCodes = productosSeleccionados.map((p) => p.CodigoProducto || 'null').join(',');

    if (previousProductCodes.value === null) {
      previousProductCodes.value = currentCodes;
      return;
    }

    if (previousProductCodes.value === currentCodes) {
      return;
    }

    previousProductCodes.value = currentCodes;

    const condicionFiscal = cotizacion.value.Cliente?.CondicionFiscal;
    const clienteExento =
      condicionFiscal && (condicionFiscal.AplicaIVA === false || condicionFiscal.AplicaIVA === 0);

    if (clienteExento) {
      if (cotizacion.value.CotizacionCantidad && cotizacion.value.CotizacionCantidad.length > 0) {
        cotizacion.value.CotizacionCantidad.forEach((cantidad) => {
          cantidad.AlicuotaIVA = 0;
        });
      }
      return;
    }

    const configIva = porcentajeIvaPredeterminado.value;
    const ivaPredeterminado = configIva ? Number(configIva) : 21;

    if (productosSeleccionados.length === 0) {
      if (cotizacion.value.CotizacionCantidad && cotizacion.value.CotizacionCantidad.length > 0) {
        cotizacion.value.CotizacionCantidad.forEach((cantidad) => {
          cantidad.AlicuotaIVA = ivaPredeterminado;
        });
      }
      return;
    }

    const productosValidos = productosSeleccionados.filter((p) => p.CodigoProducto);

    if (productosValidos.length === 0) {
      if (cotizacion.value.CotizacionCantidad && cotizacion.value.CotizacionCantidad.length > 0) {
        cotizacion.value.CotizacionCantidad.forEach((cantidad) => {
          cantidad.AlicuotaIVA = ivaPredeterminado;
        });
      }
      return;
    }

    const primerIva = productosValidos[0].AlicuotaIVA;
    if (primerIva === undefined) return;

    const ivaAAplicar = primerIva !== null ? Number(primerIva) : ivaPredeterminado;

    let tieneIvasDistintos = false;
    for (let i = 1; i < productosValidos.length; i++) {
      const ivaActual = productosValidos[i].AlicuotaIVA;
      if (ivaActual === undefined) continue;

      const ivaComparar = ivaActual !== null ? Number(ivaActual) : ivaPredeterminado;
      if (ivaComparar !== ivaAAplicar) {
        tieneIvasDistintos = true;
        break;
      }
    }

    if (tieneIvasDistintos) {
      ElNotification({
        title: 'Atención: IVAs Múltiples',
        message: `La ET tiene productos con distintos % de IVA. Se ha seleccionado el IVA del primer producto (${ivaAAplicar}%).`,
        type: 'warning',
        duration: 8000,
      });
    }

    if (cotizacion.value.CotizacionCantidad && cotizacion.value.CotizacionCantidad.length > 0) {
      cotizacion.value.CotizacionCantidad.forEach((cantidad) => {
        cantidad.AlicuotaIVA = ivaAAplicar;
      });
    }
  },
  { deep: true },
);

onMounted(() => {
  const id = Number(route.params?.id || 0);
  if (cotizacion.value.EstadoSolicitud) {
    cotizacion.value.CodigoEstado = cotizacion.value.EstadoSolicitud.Codigo;
    if (!id) {
      cotizacion.value.FechaEstado = getDate('yyyy/mm/dd');
    }
  }
});

const handleFloatingCantidadChange = (codigoCantidad) => {
  if (cotizacion.value.Codigo) {
    resultadoProduccionStore.fetchResultadoPorCantidadHTTP(cotizacion.value.Codigo, codigoCantidad);
  }
};

const sendCotizarNuevo = async () => {
  if (cotizacion.value.CodigoEstado === EstadoSolicitud.CON_PROPUESTA) {
    ElNotification({
      title: 'Validación',
      message:
        'Esta cotización está incluida en una o más propuestas al cliente, no se pueden cambiar precios',
      type: 'warning',
    });
    return;
  }

  if (!modelo.value || modelo.value.length === 0) {
    ElNotification({
      title: 'Validación',
      message: 'Para cotizar debe ingresar al menos un elemento',
      type: 'error',
    });
    return;
  }

  // VALIDACIÓN DE RECURSOS
  let procesoFaltanteRecurso = null;

  for (const elemento of modelo.value) {
    if (!elemento.Secciones) continue;

    for (const seccion of elemento.Secciones) {
      if (!Array.isArray(seccion.ElementoProceso)) continue;

      for (const proc of seccion.ElementoProceso) {
        if (proc.MarcadoParaEliminacion) continue;

        const requiereRecurso = proc.Proceso?.Recurso === true;

        if (!proc.Recurso) {
          proc.CodigoRecurso = null;
        }

        if (requiereRecurso) {
          const recursoActual = proc.Recurso?.Codigo || null;
          if (!recursoActual) {
            const recursosDisponibles = await proceso_service
              .findRecursosByProceso(proc.Proceso.Codigo)
              .then((res) => res.data)
              .catch(() => []);

            if (recursosDisponibles.length === 1) {
              proc.Recurso = recursosDisponibles[0].Recurso;
              proc.CodigoRecurso = recursosDisponibles[0].CodigoRecurso;
            } else if (recursosDisponibles.length > 1) {
              procesoFaltanteRecurso = proc.Proceso.Descripcion;
              break;
            }
          }
        }
      }
      if (procesoFaltanteRecurso) break;
    }
    if (procesoFaltanteRecurso) break;
  }

  if (procesoFaltanteRecurso) {
    ElNotification({
      title: 'Validación de Recursos',
      message: `El proceso "${procesoFaltanteRecurso}" tiene múltiples máquinas disponibles y requiere que seleccione una.`,
      type: 'error',
    });
    return;
  }

  modelo.value.forEach((elemento) => {
    const elementoBase = elemento.ElementoTipo?.CodigoElementoBase;

    const anchoMinCalculado = elemento.AnchoMinimoHojaCalculado || 0;
    const largoMinCalculado = elemento.LargoMinimoHojaCalculado || 0; // Solo aplica a Cajas

    if (elemento.Secciones) {
      elemento.Secciones.forEach((seccion) => {
        if (Array.isArray(seccion.ElementoProceso)) {
          seccion.ElementoProceso.forEach((proc) => {
            if (Array.isArray(proc.CategoriasMaterial)) {
              proc.CategoriasMaterial.forEach((cat) => {
                const relacion =
                  proc.ProcesoElementoTipoCategoriaMaterial?.find(
                    (r) => r.CodigoCategoriaMaterial === cat.Codigo,
                  ) ||
                  proc.Proceso?.ProcesoElementoTipoCategoriaMaterial?.find(
                    (r) => r.CodigoCategoriaMaterial === cat.Codigo,
                  );

                const flagAuto = cat.DimensionesAutomaticas ?? relacion?.DimensionesAutomaticas;
                const isAuto =
                  flagAuto === true || flagAuto === 1 || String(flagAuto).toLowerCase() === 'true';

                if (isAuto && Array.isArray(cat.ElementoMateriales)) {
                  cat.ElementoMateriales.forEach((matItem) => {
                    const materialRef = matItem.Material;

                    if (materialRef) {
                      const isExact =
                        materialRef.Exact === true ||
                        materialRef.Exact === 1 ||
                        String(materialRef.Exact).toLowerCase() === 'true';

                      if (isExact) {
                        if (elementoBase === ElementoBase.CAJA) {
                          if (
                            !matItem.Ancho ||
                            Number(matItem.Ancho) === 0 ||
                            Number(matItem.Ancho) < anchoMinCalculado
                          ) {
                            matItem.Ancho = anchoMinCalculado;
                          }
                          if (
                            !matItem.Largo ||
                            Number(matItem.Largo) === 0 ||
                            Number(matItem.Largo) < largoMinCalculado
                          ) {
                            matItem.Largo = largoMinCalculado;
                          }
                        } else if (
                          elementoBase === ElementoBase.ETIQUETA ||
                          elementoBase === ElementoBase.FLEXIBLE
                        ) {
                          if (
                            !matItem.Ancho ||
                            Number(matItem.Ancho) === 0 ||
                            Number(matItem.Ancho) < anchoMinCalculado
                          ) {
                            matItem.Ancho = anchoMinCalculado;
                          }
                        }
                      }
                    }
                  });
                }
              });
            }
          });
        }
      });
    }
  });

  // COTI-507: Para extrusion, el ancho de material sale de las bobinas del proceso.
  modelo.value = conAnchoMaterialExtrusion(
    modelo.value,
    ElementoTipoSeleccionado.value.CodigoElementoBase,
  );

  await nextTick();

  const tieneErrorAnchoMaterial = modelo.value.some(
    (elemento) => elemento.ErrorAnchoMaterial === true,
  );

  if (tieneErrorAnchoMaterial) {
    ElNotification({
      title: 'Validación de item',
      message:
        'El ancho del item es menor al ancho mínimo necesario para realizar el trabajo, por favor revise.',
      type: 'error',
    });
    return;
  }

  // COTI-633: registro de errores reportados por los componentes hijos (procesos,
  // materiales, pliego vs. máximo de máquina...) — reemplaza los chequeos puntuales
  // que antes vivían acá uno por uno.
  if (tieneErrores.value) {
    notificarErroresValidacion(mensajesErrores.value);
    return;
  }

  const productos = cotizacion.value.CotizacionProducto || [];
  const cantidadesCabecera = cotizacion.value.CotizacionCantidad || [];

  if (productos.length === 1 && cantidadesCabecera.length > 0) {
    const productoUnico = productos[0];

    if (!productoUnico.CotizacionProductoCantidad) {
      productoUnico.CotizacionProductoCantidad = [];
    }

    cantidadesCabecera.forEach((cabeceraQty, index) => {
      if (!productoUnico.CotizacionProductoCantidad[index]) {
        productoUnico.CotizacionProductoCantidad[index] = {
          ItemCantidad: cabeceraQty.ItemCantidad || index + 1,
          Cantidad: cabeceraQty.Cantidad,
        };
      } else {
        productoUnico.CotizacionProductoCantidad[index].Cantidad = cabeceraQty.Cantidad;
      }
    });
  }
  if (!pricesStore.cantidadesValidadas) {
    ElNotification({
      title: 'Cantidades inválidas',
      message:
        'La sumatoria de las cantidades en el detalle no coincide con las cantidades a cotizar.',
      type: 'error',
    });
    return;
  }

  const modeloLimpio = JSON.parse(JSON.stringify(modelo.value));

  // Quitar procesos eliminados del modelo limpio para la validación
  modeloLimpio.forEach((elemento) => {
    if (elemento.Secciones) {
      elemento.Secciones.forEach((seccion) => {
        if (Array.isArray(seccion.ElementoProceso)) {
          seccion.ElementoProceso = seccion.ElementoProceso.filter(
            (p) => !p.MarcadoParaEliminacion,
          );
        }
      });
    }
  });

  const primer = modeloLimpio[0];
  const cfg = elementoBaseStore.elementoBaseConfig?.campos;
  const requiereEngranaje = !!cfg?.mostrarBotonEngranaje || !!cfg?.mostrarEngranaje;

  // COTI-422: Validar engranaje solo si algun proceso tiene usaDesarrollo en true
  const algunProcesoUsaDesarrollo = modeloLimpio.some((elemento) =>
    elemento.Secciones?.some((seccion) =>
      seccion.ElementoProceso?.some((elementoProceso) => {
        const codigoRecurso = elementoProceso.CodigoRecurso || elementoProceso.Recurso?.Codigo;
        return elementoProceso.Proceso?.ProcesoRecurso?.some(
          (procesoRecurso) =>
            procesoRecurso.CodigoRecurso === codigoRecurso && procesoRecurso.UsaDesarrollo,
        );
      }),
    ),
  );

  if (requiereEngranaje && algunProcesoUsaDesarrollo) {
    const tieneEngranaje =
      Number(primer?.Engranaje || 0) > 0 || Number(primer?.Desarrollo || 0) > 0;
    if (!tieneEngranaje) {
      ElNotification({
        title: 'Validación',
        message: 'Para cotizar seleccionar un engranaje',
        type: 'error',
      });
      return;
    }
  }

  if (!cotizacion.value.CotizacionCantidad || cotizacion.value.CotizacionCantidad.length === 0) {
    cotizacion.value.CotizacionCantidad = [{ Cantidad: 1, Comision: null }];
  } else {
    // cotizacion.value.CotizacionCantidad[0].Comision = 1
  }

  // Actualizar la cotizacion con todos los datos del modelo
  cotizacion.value.CotizacionElemento = standardToModuleArray(modeloLimpio, Modules.COTIZACION);

  if (!validarCamposCotizacion()) {
    ElNotification({
      title: 'Revisar formulario',
      message: describirCamposInvalidos(pricesStore.camposCotizacionInvalidos),
      type: 'error',
    });
    return;
  }

  if (!cotizacion.value.CotizacionElemento[0].EtiquetasEje) {
    ElNotification({
      title: 'Validación',
      message: 'Para cotizar debe ingresar la cantidad de cavidades en el ancho',
      type: 'error',
    });
    return;
  }

  if (
    cotizacion.value.CotizacionElemento[0]?.ElementoTipo?.CodigoElementoBase === ElementoBase.CAJA
  ) {
    const mat = primerMaterialImpresion.value;
    if (!mat) {
      ElNotification({
        title: 'Validación',
        message:
          'No se pudo determinar el item de referencia para validar ancho/largo. Revisar parametrización.',
        type: 'error',
      });
      return;
    }

    const elemento = cotizacion.value.CotizacionElemento[0];

    const repAncho = Number(elemento.EtiquetasEje ?? 0);
    const repLargo = Number(elemento.EtiquetasDesarrollo ?? 0);

    const anchoProd = Number(elemento.MedidaEje ?? 0);
    const largoProd = Number(elemento.MedidaDesarrollo ?? 0);

    const lateralAncho = Number(elemento.Laterales ?? 0);
    const lateralLargo = Number(elemento.MargenSuperior ?? 0);

    const anchoMat = Number(mat.Ancho ?? 0);
    const largoMat = Number(mat.Largo ?? 0);

    const anchoReq = anchoProd * repAncho + lateralAncho * 2;
    const largoReq = largoProd * repLargo + lateralLargo * 2;

    const unidad = (elemento.UnidadMedida || 'mm').toLowerCase() === 'in' ? 'in' : 'mm';

    const fmt = (mm) => {
      const n = Number(mm ?? 0);
      if (unidad === 'in') return `${(n / FACTOR).toFixed(4)} in`;
      return `${n.toFixed(2)} mm`;
    };

    const errores = [];
    if (anchoMat < anchoReq) {
      errores.push(`Ancho item (${fmt(anchoMat)}) < Ancho requerido (${fmt(anchoReq)}).`);
    }
    if (largoMat < largoReq) {
      errores.push(`Largo item (${fmt(largoMat)}) < Largo requerido (${fmt(largoReq)}).`);
    }

    if (errores.length) {
      ElNotification({
        title: 'Validación',
        message: `El item no alcanza para la imposición.\n${errores.join('\n')}`,
        type: 'error',
      });
      return;
    }
  }

  useLoading().openLoading();
  try {
    if (!cotizacion.value.Codigo) {
      const nueva = await createPriceHTTP();
      // Recargar datos frescos del servidor y reinicializar el modelo para que
      // los elementos MarcadoParaEliminacion desaparezcan de la vista sin
      // necesidad de refrescar la página (el router.push puede reutilizar la
      // instancia del componente y no disparar onBeforeMount nuevamente)
      await findCotizacionPorCodigoHTTP(nueva.data.Codigo);
      await initializeModelo(cotizacion.value.CotizacionElemento, Modules.COTIZACION);
      useMessageSuccess().open(`Los datos se enviaron existosamente!`);
      await resultadoProduccionStore.fetchResultadosHTTP(nueva.data.Codigo);
      router.push(`/page/prices/edit/${nueva.data.Codigo}`);
    } else {
      await newPriceNuevoHTTP();
      if (cotizacion.value.logs && cotizacion.value.logs.length > 0) {
        replayLogs(cotizacion.value.logs);
        cotizacion.value.logs = []; // Borrar logs
      }
      useMessageSuccess().open(`Los datos se enviaron existosamente!`);

      // Recargar modelo
      await initializeModelo(cotizacion.value.CotizacionElemento, Modules.COTIZACION);
      await resultadoProduccionStore.fetchResultadosHTTP(cotizacion.value.Codigo);
    }
  } catch (err) {
    // El backend explica la causa en `message` (por ejemplo, qué procedimiento almacenado
    // falló, o qué dato falta). Se muestra tal cual y sólo se cae al texto genérico cuando
    // no llegó respuesta del servidor.
    useMessageError().open(mensajeErrorApi(err, 'Ha ocurrido un error al procesar la cotización.'));
  } finally {
    useLoading().closeLoading();
  }
};
const printPdfPropuestaComercial = () => {
  if (cotizacion.value && cotizacion.value.Codigo) {
    const pdfPath = `/${cotizacion.value.Codigo}_PropuestaComercial.pdf`;
    window.open(pdfPath, '_blank');
  } else {
    console.error('Código no disponible');
  }
};

const showPrePropuestaComercial = () => {
  // The backend enforces the same rule; this check avoids opening the modal for nothing.
  const cliente = cotizacion.value?.Cliente;
  if (!cliente?.UsuarioVendedorPrincipal?.Codigo) {
    useMessageError().open(
      `El cliente "${cliente?.Descripcion ?? ''}" no tiene un vendedor principal asignado. Asígnele uno desde la ficha del cliente y vuelva a cargar la cotización para poder generar la propuesta comercial.`,
    );
    return;
  }

  prePropuestaComercial.value = [];
  createPrePropuestaComercial();
  isModalPreProposalVisible.value = true;
};

const createPropuestaComercial = async () => {
  if (cotizacion.value && cotizacion.value.Codigo) {
    try {
      const result = await useProposalsStore().createProposalFromCotizacionHTTP(
        prePropuestaComercial.value[0].items.filter((item) => item.Codigo),
      );
      isModalPreProposalVisible.value = false;
      router.push(`/page/proposals/edit/${result.data.Codigo}`);
    } catch (err) {
      useMessageError().open(
        mensajeErrorApi(err, 'Ha ocurrido un error al intentar crear una propuesta comercial.'),
      );
    }
  }
};
const printPdfCostoInterno = async () => {
  if (esEjecutivoCotiza.value) return;
  if (cotizacion.value && cotizacion.value.Codigo) {
    isModalPdfVisible.value = true;
    isLoadingPdf.value = true;
    pdfUrl.value = '';

    try {
      const result = await getDataReportByName('costeo_interno', {
        Codigo: cotizacion.value.Codigo,
      });
      if (result && result.success && result.data && result.data.success) {
        pdfUrl.value = resolveApiUrl(result.data.url);
      } else {
        ElNotification({
          title: 'Error',
          message:
            result?.mensaje || result?.data?.mensaje || 'Hubo un problema al generar el informe.',
          type: 'error',
        });
        isModalPdfVisible.value = false;
        isLoadingPdf.value = false;
      }
    } catch (error) {
      console.error(error);
      ElNotification({
        title: 'Error',
        message: 'Ocurrió un error al solicitar el reporte.',
        type: 'error',
      });
      isModalPdfVisible.value = false;
      isLoadingPdf.value = false;
    }
  } else {
    useMessageError().open('Código de cotización no disponible');
  }
};

const verResultadosProduccion = () => {
  if (cotizacion.value && cotizacion.value.Codigo) {
    router.push(`/page/prices/resultado-produccion/${cotizacion.value.Codigo}`);
  } else {
    useMessageError().open('Código de cotización no disponible');
  }
};

const descargarPdf = () => {
  if (pdfUrl.value) {
    window.open(pdfUrl.value, '_blank');
  }
};

const onPdfLoaded = () => {
  isLoadingPdf.value = false;
};

async function onUpdateSolicitud() {
  try {
    useLoading().openLoading();
    await updateSolicitud();
    useMessageSuccess().open(`Los datos se actualizaron existosamente!`);
  } catch (err) {
    useMessageError().open('Ha ocurrido un error al actualizar los datos.');
  } finally {
    useLoading().closeLoading();
  }
}

const copiarCotizacion = () => {
  const c = cotizacion.value;

  if (!c || !c.Codigo || !c.CodigoSolicitud) {
    ElNotification({ title: 'Error', message: 'No se encontró la solicitud.', type: 'error' });
    return;
  }

  modalDuplicateVisible.value = true;
};

const ejecutarClonacion = async ({ modoCopia, actualizarET }) => {
  modalDuplicateVisible.value = false;

  const c = cotizacion.value;
  const codigoUsuario = c.CodigoUsuarioCreacion || c.CodigoUsuarioActualizacion;

  const payload = {
    UsuarioCreacion: { Codigo: codigoUsuario },
    Cliente: c.Cliente && c.Cliente.Codigo ? { Codigo: c.Cliente.Codigo } : null,
    Referencia: c.Referencia,
    SolicitudCantidad: Array.isArray(c.CotizacionCantidad)
      ? c.CotizacionCantidad.map((q) => ({
          Cantidad: q.Cantidad,
        }))
      : null,
  };

  if (modoCopia === 'costos') {
    payload.copiarCostos = true;
  } else {
    payload.actualizarET = actualizarET;
  }

  useLoading().openLoading();
  try {
    const result = await requestStore.cloneSolicitudYCotizacionHTTP(c.CodigoSolicitud, payload);

    if (!result || !result.cotizacion) {
      throw new Error('Respuesta inválida del servidor');
    }

    useMessageSuccess().open('Se creó una copia de la cotización correctamente.');
    router.push('/page/prices/list');
  } catch (err) {
    console.error(err);
    useMessageError().open(
      err?.response?.data?.message || 'Ocurrió un error al copiar la cotización.',
    );
  } finally {
    useLoading().closeLoading();
  }
};

const elementoActivo = computed(() => modelo.value?.[0] || {});
const { anchoMinimoMM } = useAnchoMinimoMM(elementoActivo);
const modalDiferenciasEtVisible = ref(false);
const diferenciasEt = ref([]);
const bloqueadoPorPropuestaEt = ref(false);
const loadingActualizarEt = ref(false);

const abrirDiferenciasEt = async () => {
  try {
    const data = await getDiferenciasEtHTTP(cotizacion.value.Codigo);
    diferenciasEt.value = data?.diferencias || [];
    bloqueadoPorPropuestaEt.value = !!data?.bloqueadoPorPropuesta;
    modalDiferenciasEtVisible.value = true;
  } catch (err) {
    console.error(err);
    useMessageError().open('Ocurrió un error al obtener las diferencias de la ET.');
  }
};

const actualizarEt = async ({ actualizarTodos, camposAActualizar }) => {
  loadingActualizarEt.value = true;
  try {
    await actualizarEtHTTP(cotizacion.value.Codigo, { actualizarTodos, camposAActualizar });
    await findCotizacionPorCodigoHTTP(cotizacion.value.Codigo);
    await initializeModelo(cotizacion.value.CotizacionElemento, Modules.COTIZACION);
    useMessageSuccess().open('Los datos se actualizaron correctamente.');
    modalDiferenciasEtVisible.value = false;
  } catch (err) {
    console.error(err);
    useMessageError().open('Ocurrió un error al actualizar los datos.');
  } finally {
    loadingActualizarEt.value = false;
  }
};

const handleRecargarCotizacionCostos = async () => {
  await findCotizacionPorCodigoHTTP(cotizacion.value.Codigo);
  await initializeModelo(cotizacion.value.CotizacionElemento, Modules.COTIZACION);
};

const datosProductoLive = computed(() => {
  const elemento = modelo.value?.[0];

  if (!elemento) return {};

  const result = {
    anchoEje: elemento.MedidaEje || 0,
    largoDesarrollo: elemento.MedidaDesarrollo || 0,
    desarrolloImposicion: elemento.Desarrollo || 0,
    repAncho: elemento.EtiquetasEje || 0,
    repAlto: elemento.EtiquetasDesarrollo || 0,
    totalBocas: (elemento.EtiquetasEje || 1) * (elemento.EtiquetasDesarrollo || 1),
    laterales: elemento.Laterales || 0,
    anchoMaterial: elemento.AnchoMaterial || 0,
    anchoMinimo: anchoMinimoMM.value || 0,
    m2: ((elemento.MedidaDesarrollo || 0) * (elemento.AnchoMaterial || 0)) / 1000000,
  };

  return result;
});

watch(
  () => {
    const el = modelo.value?.[0];
    if (!el) return null;
    return `${el.MedidaEje}-${el.MedidaDesarrollo}-${el.EtiquetasEje}-${el.EtiquetasDesarrollo}-${el.Laterales}-${el.AnchoMaterial}-${el.Desarrollo}-${el.GapEje}`;
  },
  (newVal, oldVal) => {
    if (oldVal !== null && newVal !== oldVal) {
      resultadoProduccionStore.isStale = true;
    }
  },
);

const deleteItem = ({ indexCantidad }) => {
  prePropuestaComercial.value[0].items.splice(indexCantidad, 1);
};

onBeforeMount(async () => {
  configReady.value = false;
  await configuracionStore.findAllConfiguracionesHTTP({ page: 1 });
  firstEstadoSolicitud();
  resetValidaciones();
  await firstMoneda();
  const id = route.params.id;

  if (id && +id > 0) {
    await findCotizacionPorCodigoHTTP(id);
    ElementoTipoSeleccionado.value = cotizacion.value.CotizacionElemento[0].ElementoTipo;
    setProductTypeInPrice(ElementoTipoSeleccionado.value);
    // Inicializar fileList después de cargar los datos
    const adj = cotizacion.value.CotizacionAdjunto || [];

    fileList.value = adj.map((a) => {
      const fileName = a.Url.substring(a.Url.lastIndexOf('/') + 1);
      return {
        name: fileName,
        url: a.Url,
        status: 'success',
      };
    });
    await resultadoProduccionStore.fetchResultadosHTTP(cotizacion.value.Codigo);
  } else {
    // resetCotizacion()

    console.log('grupo de elementos seleccionado:', elementoGrupo.value);
    if (
      !elementoGrupo.value ||
      !elementoGrupo.value.Codigo ||
      !elementoGrupo.value.ElementoGrupoDetalle?.length
    ) {
      ElNotification({
        title: 'Error',
        message: 'Debe seleccionar un grupo de elementos antes de crear una cotizacion.',
        type: 'error',
      });
      router.push('/page/prices/list');
      return;
    }

    ElementoTipoSeleccionado.value = elementoGrupo.value.ElementoGrupoDetalle[0].ElementoTipo;

    cotizacion.value.CotizacionElemento = elementoGrupo.value.ElementoGrupoDetalle.map((egd) => {
      return {
        ...JSON.parse(JSON.stringify(prices_elemento_model)),
        ElementoTipo: egd.ElementoTipo,
      };
    });

    const monedaPorDefecto =
      monedaList.value.find((m) => m.EsMonedaPrincipal) || monedaList.value[0];

    if (monedaPorDefecto) {
      cotizacion.value.Moneda = monedaPorDefecto;
      cotizacion.value.CodigoMoneda = monedaPorDefecto.Codigo;

      if (monedaPorDefecto.Codigo !== 1) {
        cotizacion.value.TasaDolarCompra = monedaPorDefecto.CotizacionCompra || 0;
        cotizacion.value.TasaDolarVenta = monedaPorDefecto.CotizacionVenta || 0;
      } else {
        cotizacion.value.TasaDolarCompra = 0;
        cotizacion.value.TasaDolarVenta = 0;
      }
    }
    cotizacion.value.CotizacionCantidad = [];

    const selladoDefault = sellado.value;
    const solapaDefault = solapa.value;

    cotizacion.value.CotizacionElemento.forEach((elemento, index) => {
      if (index === 0) {
        if (
          elemento.Sellado === 0 &&
          ElementoTipoSeleccionado.value.CodigoElementoBase === ElementoBase.BOLSAS_BOX
        ) {
          elemento.Sellado = selladoDefault;
        }
        if (
          elemento.Solapa === 0 &&
          [ElementoBase.BOLSAS_FUELLE, ElementoBase.BOLSAS_BOX].includes(
            ElementoTipoSeleccionado.value.CodigoElementoBase,
          )
        ) {
          elemento.Solapa = solapaDefault;
        }
      }
    });

    await pricesStore.setDefaults(elementoGrupo.value.Codigo);
  }
  await elementoBaseStore.findElementoBaseConfigHTTP(
    ElementoTipoSeleccionado.value.CodigoElementoBase,
    Modules.COTIZACION,
  );
  await initializeModelo(cotizacion.value.CotizacionElemento, Modules.COTIZACION);
  configReady.value = true;
  materiaPrimaFamiliaStore.emptyList();
  materiaPrimaFamiliaStore.findAllfamiliaMaterialHTTP({
    page: 0,
    sortBy: 'Descripcion',
    sort: 'asc',
  });

  unidadMedidaStore.emptyList();
  unidadMedidaStore.findAllUnidadMedidaHTTP({
    EsUnidadVenta: true,
    page: 0,
    Activo: true,
    CodigoElementoTipo: ElementoTipoSeleccionado.value.Codigo,
  });

  usabilidadStore.emptyList();
  usabilidadStore.findAllUsabilidadHTTP({
    page: 0,
    CodigoElementoTipo: ElementoTipoSeleccionado.value.Codigo,
  });

  // console.log(ElementoTipoSeleccionado.value)
});

onBeforeUnmount(() => {
  resetCotizacion();
  resetValidaciones();
  resultadoProduccionStore.reset();
});
</script>

<style scoped>
.product-tabs {
  margin-top: 10px;
}
.detail-container {
  position: relative;
  background-color: white;
  border: 3px solid lightgray;
  padding: 20px;
}

.labelClassName {
  text-align: center;
  font-weight: bold;
}

.custom-label {
  font-weight: 700;
  color: #111;
}

.quantity-inputs {
  display: flex;
  flex-wrap: nowrap;
}

.container-quantity-inputs {
  display: flex;
  overflow-x: auto;
  width: 100%;
}

/* Estilo para el botón */
.el-button-quantity {
  flex-shrink: 0;
  margin: 0;
}

.grilla-input-wrapper :deep(.el-row) {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
}

.grilla-input-wrapper :deep(.el-row::before),
.grilla-input-wrapper :deep(.el-row::after) {
  display: none !important;
}

.grilla-input-wrapper :deep(.el-input-number) {
  flex: 1 1 auto !important;
  width: 100% !important;
  min-width: 0 !important;
}

.grilla-input-wrapper :deep(.el-input__inner) {
  text-align: right !important;
}

.grilla-input-wrapper :deep(.unit) {
  flex-shrink: 0 !important;
  margin-left: 6px !important;
  white-space: nowrap !important;
  line-height: 1 !important;
}

.grilla-input-wrapper :deep(.el-input__wrapper) {
  padding-left: 6px !important;
  padding-right: 6px !important;
}

.container {
  margin-top: 2%;
  display: flex;
  justify-content: flex-end;
}

.container--nueva-grilla {
  align-items: center;
}

.container--nueva-grilla .accion-cotizar {
  order: -1;
}

.container--nueva-grilla .accion-cotizar :deep(.el-button) {
  font-weight: 600;
  padding-left: 28px;
  padding-right: 28px;
}

.container--nueva-grilla .accion-copiar-cotizacion {
  order: 1;
  margin-left: 12px;
  margin-right: 0 !important;
}

.solicitud-link {
  color: blue;
  text-decoration: none;
  cursor: pointer;
}
.alerta-banner-dark {
  margin-bottom: 20px;
}

:deep(.el-alert__content) {
  width: 100%;
}

.banner-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 16px;
}

.banner-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #ffffff;
}

.banner-title {
  font-size: 14px;
  font-weight: bold;
}

.banner-description {
  font-size: 13px;
}

.banner-button {
  color: var(--el-color-warning);
  font-weight: 600;
  border: none;
}

.banner-button:hover {
  color: var(--el-color-warning-light-3);
  background-color: var(--el-color-warning-light-9);
}
</style>
